
import { createSlice } from '@reduxjs/toolkit';

// Campos de la consulta
const campos = {
    motivo: ['datosGenerales', 'motivo'],
    peea: ['datosGenerales', 'peea'],

    habitusExterior: ['datosGenerales', 'exploracionFisica', 'habitusExterior'],
    cabezaYCuello: ['datosGenerales', 'exploracionFisica', 'cabezaYCuello'],
    torax: ['datosGenerales', 'exploracionFisica', 'torax'],
    abdomen: ['datosGenerales', 'exploracionFisica', 'abdomen'],
    extremidades: ['datosGenerales', 'exploracionFisica', 'extremidades'],
    piel: ['datosGenerales', 'exploracionFisica', 'piel'],
    genitales: ['datosGenerales', 'exploracionFisica', 'genitales'],
    otro: ['datosGenerales', 'exploracionFisica', 'otro'],

    fc: ['datosGenerales', 'signosVitales', 'fc'],
    fr: ['datosGenerales', 'signosVitales', 'fr'],
    ta: ['datosGenerales', 'signosVitales', 'ta'],
    t: ['datosGenerales', 'signosVitales', 't'],
    saO2: ['datosGenerales', 'signosVitales', 'saO2'],
    peso: ['datosGenerales', 'signosVitales', 'peso'],
    talla: ['datosGenerales', 'signosVitales', 'talla'],
    imc: ['datosGenerales', 'signosVitales', 'imc'],
    fem: ['datosGenerales', 'signosVitales', 'fem'],

    citaFecha: ['proximaCita', 'fecha'],
    citaHora: ['proximaCita', 'hora'],
    citaMotivo: ['proximaCita', 'motivo'],
    citaDuracion: ['proximaCita', 'duracion'],
    citaComentario: ['proximaCita', 'comentario'],

    pronostico: ['pronostico'],
    comentarios: ['comentarios'],
    diagnosticosData: ['diagnosticosData'],
    diagnosticos: ['diagnosticos'],
    medicamentos: ['medicamentos'],
    estudios: ['estudios']
};

// Obtiene el valor de una propiedad anidada
const getValue = (obj, path) => {
    return path.reduce((acc, key) => acc?.[key], obj);
};

// Modifica una propiedad anidada
const setValue = (obj, path, value) => {
    const lastKey = path[path.length - 1];

    const target = path
        .slice(0, -1)
        .reduce((acc, key) => acc?.[key], obj);

    if (target) {
        target[lastKey] = value;
    }
};

// Crea la estructura inicial de una consulta
const createInitialConsulta = (id) => ({
    paciente: id,
    inicializado: false,

    datosGenerales: {
        motivo: '',
        peea: '',

        exploracionFisica: {
            habitusExterior: '',
            cabezaYCuello: '',
            torax: '',
            abdomen: '',
            extremidades: '',
            piel: '',
            genitales: '',
            otro: ''
        },

        signosVitales: {
            fc: '',
            fr: '',
            ta: '',
            t: '',
            saO2: '',
            peso: '',
            talla: '',
            imc: '0',
            fem: ''
        }
    },

    proximaCita: {
        fecha: '',
        hora: '',
        motivo: '',
        duracion: '',
        comentario: ''
    },

    diagnosticosData: '',
    estudios: [],
    pronostico: '',
    comentarios: ''
});

// Medicamento vacío utilizado cuando el backend no devuelve medicamentos
const createInitialMedicamento = () => ({
    id: Math.floor(Date.now() / 1000) + '|||nuevo|||',
    medicamento: '',
    text: '',
    inicio: '',
    administracionPrescrita: false,
    modalidadPrescripcion: 'Con horario',
    dosis: 1,
    frecuencia: '2',
    tipoFrecuencia: 'Horas',

    diasSemana: {
        lunes: false,
        martes: false,
        miercoles: false,
        jueves: false,
        viernes: false,
        sabado: false,
        domingo: false
    },

    tipoDuracion: 'Días',
    duracion: 1,
    dosisRescate: false,
    contadorDosis: false,
    contadorDosisDisponibles: 0,
    contadorDosisLimite: 0,
    activo: true,
    validation: false
});

// Configuración de los campos que tienen acciones init individuales
const camposInit = {
    motivo: 'motivo',
    peea: 'peea',

    habitusExterior: 'exploracionFisica',
    cabezaYCuello: 'exploracionFisica',
    torax: 'exploracionFisica',
    abdomen: 'exploracionFisica',
    extremidades: 'exploracionFisica',
    piel: 'exploracionFisica',
    genitales: 'exploracionFisica',
    otro: 'exploracionFisica',

    fc: 'signosVitales',
    fr: 'signosVitales',
    ta: 'signosVitales',
    t: 'signosVitales',
    saO2: 'signosVitales',
    peso: 'signosVitales',
    talla: 'signosVitales',
    imc: 'signosVitales',
    fem: 'signosVitales',

    pronostico: 'pronostico',
    comentarios: 'comentarios'
};

// Genera los reducers init existentes
const initReducers = {};

Object.entries(camposInit).forEach(([campo, origen]) => {
    const nombre = campo.charAt(0).toUpperCase() + campo.slice(1);

    initReducers[`init${nombre}`] = (state, { payload }) => {
        const consulta = state[payload.id];

        if (!consulta) return;

        const valor = origen === campo
            ? payload[campo]
            : payload[origen]?.[campo];

        if (
            getValue(consulta, campos[campo]) !== undefined &&
            valor !== undefined
        ) {
            setValue(consulta, campos[campo], valor);
        }
    };
});

// Genera los reducers set y clear existentes
const fieldReducers = {};

Object.entries(campos).forEach(([campo, path]) => {
    const nombre = campo.charAt(0).toUpperCase() + campo.slice(1);

    // Los nombres de las propiedades de próxima cita en los payloads originales no tienen el prefijo cita.
    const payloadKey = campo.startsWith('cita')
        ? path[path.length - 1]
        : campo;

    fieldReducers[`set${nombre}`] = (state, { payload }) => {
        const consulta = state[payload.paciente];

        if (!consulta) return;

        setValue(consulta, path, payload[payloadKey]);
    };

    fieldReducers[`clear${nombre}`] = (state, { payload }) => {
        const consulta = state[payload];

        if (!consulta) return;

        const emptyValue = [
            'diagnosticos',
            'medicamentos',
            'estudios'
        ].includes(campo) ? [] : '';

        setValue(consulta, path, emptyValue);
    };
});

export const consultaSlice = createSlice({
    name: 'consulta',

    initialState: {},

    reducers: {

        // Inicializa la estructura del paciente solamente cuando no existe
        initPaciente: (state, { payload }) => {
            if (typeof state[payload.id] === 'undefined') {
                state[payload.id] = createInitialConsulta(payload.id);
            }
        },

        // Elimina completamente la consulta temporal
        clearPaciente: (state, { payload }) => {
            delete state[payload];
        },

        // Inicialización única utilizando el backend
        initConsultaData: (state, { payload }) => {
            const { id, data } = payload;

            const consulta = state[id];

            if (!consulta || consulta.inicializado || !data) {
                return;
            }

            const datosGenerales = consulta.datosGenerales;

            datosGenerales.motivo = data.motivo ?? '';
            datosGenerales.peea = data.peea ?? '';

            const exploracion = data.exploracionFisica ?? {};

            Object.keys(datosGenerales.exploracionFisica).forEach(campo => {
                datosGenerales.exploracionFisica[campo] =
                    exploracion[campo] ?? '';
            });

            const signos = data.signosVitales ?? {};

            Object.keys(datosGenerales.signosVitales).forEach(campo => {
                datosGenerales.signosVitales[campo] =
                    signos[campo] ??
                    (campo === 'imc' ? '0' : '');
            });

            consulta.diagnosticos = Array.isArray(data.pacienteDiagnosticos)
                ? data.pacienteDiagnosticos
                : [];

            consulta.diagnosticosData = data.diagnosticosData ?? '';

            consulta.medicamentos = (
                Array.isArray(data.pacienteMedicamentos) &&
                data.pacienteMedicamentos.length > 0
            )
                ? data.pacienteMedicamentos
                : [createInitialMedicamento()];

            consulta.pronostico = data.pronostico ?? '';
            consulta.comentarios = data.comentarios ?? '';

            consulta.inicializado = true;
        },

        // Acciones init individuales que ya existían
        ...initReducers,

        // Acciones set y clear de cada campo
        ...fieldReducers,

        // Inicialización de diagnósticos
        initDiagnosticos: (state, { payload }) => {
            const consulta = state[payload.id];

            if (!consulta) return;

            if (typeof consulta.diagnosticos === 'undefined') {
                consulta.diagnosticos = payload.diagnosticos;
                consulta.diagnosticosData = payload.diagnosticosData;
            }
        },

        // Inicialización de medicamentos
        initMedicamentos: (state, { payload }) => {
            const consulta = state[payload.id];

            if (!consulta) return;

            if (typeof consulta.medicamentos === 'undefined') {
                consulta.medicamentos = payload.medicamentos;
            }
        }
    }
});

// Acciones de inicialización.
export const {
    initPaciente,
    clearPaciente,
    initConsultaData,

    initMotivo,
    initPeea,
    initHabitusExterior,
    initCabezaYCuello,
    initTorax,
    initAbdomen,
    initExtremidades,
    initPiel,
    initGenitales,
    initOtro,

    initFc,
    initFr,
    initTa,
    initT,
    initSaO2,
    initPeso,
    initTalla,
    initImc,
    initFem,

    initPronostico,
    initComentarios,
    initDiagnosticos,
    initMedicamentos,

    // Acciones de modificación
    setMotivo,
    setPeea,
    setHabitusExterior,
    setCabezaYCuello,
    setTorax,
    setAbdomen,
    setExtremidades,
    setPiel,
    setGenitales,
    setOtro,

    setFc,
    setFr,
    setTa,
    setT,
    setSaO2,
    setPeso,
    setTalla,
    setImc,
    setFem,

    setCitaFecha,
    setCitaHora,
    setCitaMotivo,
    setCitaDuracion,
    setCitaComentario,

    setPronostico,
    setComentarios,
    setDiagnosticos,
    setDiagnosticosData,
    setMedicamentos,
    setEstudios,

    // Acciones de limpieza
    clearMotivo,
    clearPeea,
    clearHabitusExterior,
    clearCabezaYCuello,
    clearTorax,
    clearAbdomen,
    clearExtremidades,
    clearPiel,
    clearGenitales,
    clearOtro,

    clearFc,
    clearFr,
    clearTa,
    clearT,
    clearSaO2,
    clearPeso,
    clearTalla,
    clearImc,
    clearFem,

    clearCitaFecha,
    clearCitaHora,
    clearCitaMotivo,
    clearCitaDuracion,
    clearCitaComentario,

    clearPronostico,
    clearComentarios,
    clearDiagnosticos,
    clearDiagnosticosData,
    clearMedicamentos,
    clearEstudios

} = consultaSlice.actions;